// Pure functions, no Angular DI — same convention as offline-cache-key.ts and
// offline-feature-gate.ts.
//
// Saved videos live as plain MP4 files in the Origin Private File System (OPFS), one file per
// video, under:
//
//   impata-offline-video/<school>/<user>/<file>
//
// The IndexedDB `media` store keeps only a small manifest per video (version, size, file
// name). A manifest is written ONLY after the file is completely on disk, so a file without a
// manifest is by definition an interrupted download and is safe to delete (sweepOrphanFiles).
//
// Why files and not IndexedDB rows: a real file can be handed to <video> through
// URL.createObjectURL(file), and the browser plays and seeks it natively. Bytes in IndexedDB
// needed a hand-written service worker to serve them, and that is the piece that never worked
// on real devices. Trade-off, accepted deliberately: OPFS is private to this origin but not
// encrypted at rest. The old encryption key lived in the same browser storage, so anyone able
// to read the files could already read the key.

export const VIDEO_ROOT_DIR = 'impata-offline-video';
const WRITER_WORKER_URL = '/assets/offline-video-writer.js';

// Every OPFS call from the page goes through this. A storage call that never settles must not
// hold up the page that made it — the video player waits on these. Same rule as the IndexedDB
// layer: nothing event- or storage-based is awaited without a ceiling.
const STORAGE_TIMEOUT_MS = 4000;

function bounded<T>(promise: Promise<T>, fallback: T, ms = STORAGE_TIMEOUT_MS): Promise<T> {
  return new Promise<T>((resolve) => {
    const timer = setTimeout(() => resolve(fallback), ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      () => {
        clearTimeout(timer);
        resolve(fallback);
      },
    );
  });
}

// An interrupted download leaves a file with no manifest. A download still IN PROGRESS looks
// exactly the same, possibly in another frame or tab, so the orphan sweep leaves anything
// touched recently alone.
const ORPHAN_MIN_AGE_MS = 30 * 60 * 1000;

// OPFS names may not contain path separators, and ':' is awkward on some platforms. Ids are
// opaque, so a lossless-enough substitution is all that is needed; the manifest records the
// exact name used, so nothing ever has to reverse this.
function safeName(part: string): string {
  return String(part).replace(/[^A-Za-z0-9._-]/g, '_');
}

export function videoDirPath(school: string, user: number): string[] {
  return [VIDEO_ROOT_DIR, safeName(school), safeName(String(user))];
}

export function videoFileName(resourceId: string, rendition: string, version: string): string {
  return `${safeName(resourceId)}__${safeName(rendition)}__${safeName(version)}.mp4`;
}

// Cheap, synchronous capability check: does this browser have OPFS and workers at all? Private
// windows in Firefox and Safari pass this but then refuse getDirectory() — see
// probeVideoStorage for the definitive answer.
export function offlineVideoStorageAvailable(): boolean {
  return typeof Worker !== 'undefined' && typeof (navigator as any)?.storage?.getDirectory === 'function';
}

let probePromise: Promise<boolean> | null = null;

// Actually opens the storage root once per page. False in private/incognito windows and on
// browsers without OPFS. Never throws.
export function probeVideoStorage(): Promise<boolean> {
  if (!probePromise) {
    probePromise = offlineVideoStorageAvailable()
      ? bounded(
          (navigator as any).storage.getDirectory().then(() => true),
          false,
        )
      : Promise.resolve(false);
  }
  return probePromise;
}

// Null when the directory is not there (NotFoundError when create is false), there is no OPFS,
// or storage did not answer in time.
function getDir(path: string[], create: boolean): Promise<any | null> {
  if (!offlineVideoStorageAvailable()) return Promise.resolve(null);
  return bounded(
    (async () => {
      let dir = await (navigator as any).storage.getDirectory();
      for (const part of path) {
        dir = await dir.getDirectoryHandle(part, { create });
      }
      return dir;
    })(),
    null,
  );
}

// The saved video as a File, ready for URL.createObjectURL. Null if it is not there.
export async function openVideoFile(school: string, user: number, fileName: string): Promise<File | null> {
  const dir = await getDir(videoDirPath(school, user), false);
  if (!dir) return null;
  return bounded<File | null>(
    dir.getFileHandle(fileName).then((handle: any) => handle.getFile()),
    null,
  );
}

export async function deleteVideoFile(school: string, user: number, fileName: string): Promise<void> {
  const dir = await getDir(videoDirPath(school, user), false);
  if (!dir) return;
  await bounded(dir.removeEntry(fileName), undefined); // already gone is fine
}

// Every saved video for every user on this device. Used by logout and "Remove all", which
// already wipe all of IndexedDB the same way.
export async function deleteAllVideoFiles(): Promise<void> {
  if (!offlineVideoStorageAvailable()) return;
  // Nothing saved, or no OPFS, both end up at the fallback.
  await bounded(
    (navigator as any).storage.getDirectory().then((root: any) => root.removeEntry(VIDEO_ROOT_DIR, { recursive: true })),
    undefined,
  );
}

// Deletes this user's files that no manifest refers to: interrupted downloads, and files whose
// manifest was removed without the file (a crash between the two deletes). Never throws.
export async function sweepOrphanFiles(school: string, user: number, keep: Set<string>): Promise<number> {
  const dir = await getDir(videoDirPath(school, user), false);
  if (!dir || typeof dir.keys !== 'function') return 0;
  let removed = 0;
  try {
    const names: string[] = await bounded(
      (async () => {
        const found: string[] = [];
        for await (const name of dir.keys()) found.push(name);
        return found;
      })(),
      [],
    );
    for (const name of names) {
      if (keep.has(name)) continue;
      const file: File | null = await bounded(
        dir.getFileHandle(name).then((h: any) => h.getFile()),
        null,
      );
      // Unreadable (locked by a writer) or recently written: possibly a download in progress.
      if (!file || Date.now() - file.lastModified < ORPHAN_MIN_AGE_MS) continue;
      const ok = await bounded(dir.removeEntry(name).then(() => true), false);
      if (ok) removed++;
    }
  } catch (err) {
    console.error('[offline] orphan video sweep failed', err);
  }
  return removed;
}

export interface VideoDownloadHandle {
  done: Promise<number>; // resolves with the byte count once the file is fully on disk
  cancel: () => void;
}

// Streams `url` straight to disk in a worker (see assets/offline-video-writer.js for why a
// worker). Rejects with an Error whose name is preserved, so callers can tell
// QuotaExceededError apart from a network failure.
export function downloadVideoToFile(
  url: string,
  school: string,
  user: number,
  fileName: string,
  onProgress?: (received: number, total: number) => void,
): VideoDownloadHandle {
  const worker = new Worker(WRITER_WORKER_URL);
  let settle: ((err?: Error, bytes?: number) => void) | null = null;

  const done = new Promise<number>((resolve, reject) => {
    settle = (err?: Error, bytes?: number) => {
      settle = null;
      worker.terminate();
      if (err) reject(err);
      else resolve(bytes ?? 0);
    };

    worker.onmessage = (event: MessageEvent) => {
      const msg = event.data || {};
      if (msg.type === 'progress') onProgress?.(msg.received, msg.total);
      else if (msg.type === 'done') settle?.(undefined, msg.bytes);
      else if (msg.type === 'error') settle?.(Object.assign(new Error(msg.message), { name: msg.name }));
    };
    worker.onerror = (event: ErrorEvent) => {
      event.preventDefault();
      settle?.(new Error(event.message || 'The download worker failed to start.'));
    };
  });

  worker.postMessage({ type: 'download', url, dir: videoDirPath(school, user), fileName });

  return {
    done,
    // Terminating mid-write can leave a partial file. It has no manifest, so it is invisible
    // and is removed by the next sweepOrphanFiles.
    cancel: () => settle?.(Object.assign(new Error('Download cancelled'), { name: 'AbortError' })),
  };
}
